import { useState } from 'react';

import {
  Button,
  ClosedEyeIcon,
  CopyIcon,
  Label,
  OpenedEyeIcon,
  TTranslator,
} from '@smartshell/ui-kit';
import { Column } from 'react-table';

import { PermissionKey } from 'auth/types';
import { TLocalesKeys } from 'localization';
import { TGameAccount } from 'types';

import { ICON_PROPS } from '../constants';
import s from './GameAccountsTable.module.scss';

export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const el = document.createElement('span');
    el.textContent = text;
    el.style.all = 'unset';
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.clip = 'rect(0, 0, 0, 0)';
    el.style.whiteSpace = 'pre';
    el.style.webkitUserSelect = 'text';
    el.style.userSelect = 'text';

    document.body.appendChild(el);

    const range = document.createRange();
    range.selectNodeContents(el);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    document.execCommand('copy');

    selection?.removeAllRanges();
    document.body.removeChild(el);
  } catch (err) {
    console.error('Copy failed', err);
  }
};

export const useColumnsTable = (
  t: TTranslator<TLocalesKeys>,
  permissions: Set<PermissionKey> | null
): Array<Column<TGameAccount>> => [
  {
    Header: t('login'),
    accessor: 'login',
    Cell: ({ value }) => (
      <div className={s.login}>
        <span>{value}</span>
        <Button
          variant="ghost"
          className={s.control}
          onClick={() => copyToClipboard(value)}
        >
          <CopyIcon {...ICON_PROPS} />
        </Button>
      </div>
    ),
  },
  {
    Header: t('password'),
    accessor: 'password',
    Cell: ({ value }: { value: string }): JSX.Element => {
      const [isVisible, setIsVisible] = useState(false);

      return (
        <div className={s.password}>
          <span>{isVisible ? value : '••••••••••'}</span>
          {permissions?.has('game_account_password_view') && (
            <>
              <Button
                variant="ghost"
                onClick={() => copyToClipboard(value)}
                className={s.control}
              >
                <CopyIcon {...ICON_PROPS} />
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsVisible(!isVisible)}
                className={s.control}
              >
                {isVisible ? (
                  <OpenedEyeIcon {...ICON_PROPS} />
                ) : (
                  <ClosedEyeIcon {...ICON_PROPS} />
                )}
              </Button>
            </>
          )}
        </div>
      );
    },
  },
  {
    Header: t('status'),
    accessor: 'active',
    Cell: ({ value }) =>
      t(value ? 'account.status.active' : 'account.status.notActive'),
  },
  {
    Header: t('state'),
    accessor: 'busy',
    Cell: ({ value }) => (
      <Label
        text={t(value ? 'host.statuses.busy' : 'host.statuses.free')}
        color={value ? 'red' : 'green'}
      />
    ),
  },
  {
    id: 'pc',
    Header: t('accounts.pc.use'),
    accessor: (row) => row.host?.alias,
  },
  {
    id: 'assignment',
    Header: t('accounts.pc.assignment'),
    accessor: (row) => row?.assignment?.host?.alias,
  },
];
